/**
 * ClubsPage - Club Discovery & Directory
 *
 * Public directory with search, filters, and grid layout for club discovery.
 * Rebuilt to match Figma Nightfall Kit design.
 *
 * Issue #635, #1355: Added meeting mode and sport filters for discovery.
 * Reference: docs/design/FIGMA_IMPLEMENTATION_PLAN.md - Phase 7D
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { TopBar, Input, EmptyState, ClubCardSkeleton } from '../components';
import { useClubs, useDiscoverClubs } from '../hooks/useClubs';
import { useRegisterFab } from '../contexts/PWAContext';
import type { MeetingMode } from '../lib/api/clubs';
import { Search, Users, Calendar, Globe, Plus, AlertTriangle } from 'lucide-react';

type FilterTab = 'discover' | 'my-clubs';
type MeetingModeFilter = 'all' | 'in_person' | 'virtual' | 'hybrid';

// Popular sports for quick filter chips
const POPULAR_SPORTS = ['Running', 'Cycling', 'Trail Running', 'Triathlon', 'Swimming'];

// Club Card Component for grid
interface ClubCardProps {
  club: {
    id: string;
    name: string;
    description?: string | null;
    slug: string;
    member_count: number;
    upcoming_event_count?: number;
    role?: 'leader' | 'member' | 'moderator' | 'owner';
    hero_image_url?: string | null;
    primary_sport?: string | null;
    meeting_mode?: MeetingMode;
  };
  onClick: () => void;
}

function ClubCard({ club, onClick }: ClubCardProps) {
  const isJoined = club.role === 'leader' || club.role === 'member';
  const isVirtual = club.meeting_mode === 'virtual';
  const isHybrid = club.meeting_mode === 'hybrid';

  return (
    <div
      className="bg-oro-surface border border-oro-border rounded-2xl overflow-hidden cursor-pointer hover:border-accent/30 transition-colors shadow-card"
      onClick={onClick}
    >
      {/* Hero Image */}
      <div className="relative h-32 bg-oro-surface-alt">
        {club.hero_image_url ? (
          <img
            src={club.hero_image_url}
            alt={club.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="flex items-center justify-center h-full text-text-muted">
            <Users className="w-8 h-8" />
          </div>
        )}
        {/* Badges */}
        <div className="absolute top-3 right-3 flex gap-2">
          {(isVirtual || isHybrid) && (
            <div className="flex items-center gap-1 px-2 py-1 bg-oro-surface/90 backdrop-blur-sm text-text-secondary text-xs font-medium rounded-full">
              <Globe className="w-3 h-3" />
              {isHybrid ? 'Hybrid' : 'Virtual'}
            </div>
          )}
          {isJoined && (
            <div className="px-2 py-1 bg-accent/20 text-accent text-xs font-medium rounded-full">
              Joined
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="text-base text-text-primary font-medium mb-2 line-clamp-2">
          {club.name}
        </h3>

        <p className="text-sm text-text-muted mb-3 line-clamp-2">
          {club.description || 'No description available'}
        </p>

        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs text-text-secondary">
            <Users className="w-4 h-4 shrink-0" />
            <span>{club.member_count} {club.member_count === 1 ? 'member' : 'members'}</span>
          </div>

          {club.upcoming_event_count !== undefined && club.upcoming_event_count > 0 && (
            <div className="flex items-center gap-2 text-xs text-accent">
              <Calendar className="w-4 h-4 shrink-0" />
              <span>{club.upcoming_event_count} upcoming {club.upcoming_event_count === 1 ? 'event' : 'events'}</span>
            </div>
          )}

          {club.primary_sport && (
            <div className="flex items-center gap-2 text-xs text-text-muted">
              <span className="capitalize">{club.primary_sport}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ClubsPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<FilterTab>('discover');
  const [meetingModeFilter, setMeetingModeFilter] = useState<MeetingModeFilter>('all');
  const [sportFilter, setSportFilter] = useState<string | null>(null);

  // Build discover params from filters
  const discoverParams = useMemo(() => {
    const params: { sport?: string; meeting_mode?: 'in_person' | 'virtual' | 'hybrid' } = {};
    if (sportFilter) params.sport = sportFilter;
    if (meetingModeFilter !== 'all') params.meeting_mode = meetingModeFilter;
    return params;
  }, [sportFilter, meetingModeFilter]);

  // Fetch data based on active tab
  const { data: myClubs, isLoading: myClubsLoading, error: myClubsError } = useClubs();
  const { data: discoveredClubs, isLoading: discoverLoading, error: discoverError } = useDiscoverClubs(
    activeTab === 'discover' ? discoverParams : undefined
  );

  // Determine which data to show
  const isLoading = activeTab === 'discover' ? discoverLoading : myClubsLoading;
  const error = activeTab === 'discover' ? discoverError : myClubsError;

  // The create-club FAB renders unconditionally on this page (a plain button at
  // the end of the return, not tab- or role-gated), so suppress the PWA install
  // prompt whenever ClubsPage is mounted (#3205; mechanism #3096/#3200). CONTRACT:
  // if that FAB ever becomes conditional, pass the SAME render condition here, not
  // a bare `true` — else the prompt over-suppresses where no FAB shows (cf.
  // EventsTab's `useRegisterFab(isLeaderOfAnyClub)`).
  useRegisterFab(true);

  // Filter and search clubs
  const filteredClubs = useMemo(() => {
    const clubs = activeTab === 'discover' ? discoveredClubs : myClubs;
    if (!clubs) return [];

    let filtered = [...clubs];

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (club) =>
          club.name.toLowerCase().includes(query) ||
          (club.description && club.description.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [activeTab, discoveredClubs, myClubs, searchQuery]);

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar title="Clubs" />

      <div className="flex-1 overflow-y-auto pt-14">
        <div className="px-4 py-6 space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
            <Input
              type="text"
              placeholder="Search clubs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              size="lg"
            />
          </div>

          {/* Main Tabs */}
          <div className="flex gap-2 border-b border-oro-border -mx-4 px-4" data-tour="clubs-list">
            {[
              { key: 'discover' as const, label: 'Discover' },
              { key: 'my-clubs' as const, label: 'My Clubs' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === tab.key
                    ? 'text-accent border-b-2 border-accent'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Filter Chips - Only shown in Discover tab */}
          {activeTab === 'discover' && (
            <div className="space-y-3">
              {/* Meeting Mode Filter */}
              <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4">
                {[
                  { key: 'all' as const, label: 'All Clubs' },
                  { key: 'in_person' as const, label: 'In Person' },
                  { key: 'virtual' as const, label: 'Virtual' },
                  { key: 'hybrid' as const, label: 'Hybrid' },
                ].map((mode) => (
                  <button
                    key={mode.key}
                    onClick={() => setMeetingModeFilter(mode.key)}
                    className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                      meetingModeFilter === mode.key
                        ? 'bg-accent text-black font-medium'
                        : 'bg-oro-surface border border-oro-border text-text-secondary hover:border-oro-border-hover'
                    }`}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>

              {/* Sport Filter */}
              <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4">
                <button
                  onClick={() => setSportFilter(null)}
                  className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                    !sportFilter
                      ? 'bg-accent text-black font-medium'
                      : 'bg-oro-surface border border-oro-border text-text-secondary hover:border-oro-border-hover'
                  }`}
                >
                  All Sports
                </button>
                {POPULAR_SPORTS.map((sport) => (
                  <button
                    key={sport}
                    onClick={() => setSportFilter(sport)}
                    className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                      sportFilter === sport
                        ? 'bg-accent text-black font-medium'
                        : 'bg-oro-surface border border-oro-border text-text-secondary hover:border-oro-border-hover'
                    }`}
                  >
                    {sport}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <ClubCardSkeleton key={i} />
              ))}
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="py-12">
              <EmptyState
                icon={<AlertTriangle className="w-12 h-12" />}
                title="Failed to load clubs"
                message={error instanceof Error ? error.message : 'Something went wrong'}
                actionLabel='Try Again' onAction={() => window.location.reload()}
              />
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && filteredClubs.length === 0 && (
            <div className="py-12">
              <EmptyState
                icon={<Users className="w-12 h-12" />}
                title={
                  searchQuery
                    ? 'No clubs found'
                    : activeTab === 'my-clubs'
                      ? 'No joined clubs'
                      : sportFilter || meetingModeFilter !== 'all'
                        ? 'No matching clubs'
                        : 'No clubs available'
                }
                message={
                  searchQuery
                    ? `No clubs match "${searchQuery}"`
                    : activeTab === 'my-clubs'
                      ? 'Join some clubs to see them here'
                      : sportFilter || meetingModeFilter !== 'all'
                        ? 'Try adjusting your filters'
                        : 'Be the first to create a club'
                }
                actionLabel="Create Club"
                onAction={() => navigate('/clubs/create')}
              />
            </div>
          )}

          {/* Clubs Grid */}
          {!isLoading && !error && filteredClubs.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {filteredClubs.map((club) => (
                <ClubCard
                  key={club.id}
                  club={club}
                  onClick={() => navigate(`/clubs/${club.slug}`)}
                />
              ))}
            </div>
          )}

          {/* Create Club FAB */}
          <button
            onClick={() => navigate('/clubs/create')}
            className="fixed bottom-24 left-4 w-11 h-11 bg-accent hover:bg-accent/90 text-black rounded-2xl shadow-float flex items-center justify-center transition-all duration-200 hover:shadow-float-lg"
            aria-label="Create Club"
          >
            <Plus className="w-6 h-6" />
          </button>
        </div>
      </div>
    </div>
  );
}
